import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, Group, Text, Textarea } from '@mantine/core';
import {
  IconEraser,
  IconRobot,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { useUser } from '@/contexts/user-context';
import {
  chatErrorMessage,
  clearChatHistory,
  fetchChatHistory,
  sendChatTurn,
  type ChatMessage,
  type ChatTurn,
} from '@/beta/api/chat-api';

const SUGGESTIONS = [
  'What’s the most valuable set in my collection?',
  'How much is my collection worth?',
  'Show me my top cards by value.',
];

function TypingDots() {
  return (
    <Group gap={5} aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="beta-skeleton"
          style={{ width: 7, height: 7, borderRadius: '50%', animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </Group>
  );
}

function Avatar() {
  return (
    <span
      aria-hidden
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: 'var(--beta-inset)',
        border: '1px solid var(--beta-border)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--beta-accent-7)',
        flexShrink: 0,
      }}
    >
      <IconRobot size={14} />
    </span>
  );
}

/** Index of the nearest user message before `upto`, or -1 when there is none. */
function nearestUserIndex(messages: ChatMessage[], upto: number): number {
  for (let i = upto - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'user') return i;
  }
  return -1;
}

/**
 * Professor Elm — the floating, bottom-right AI assistant. Logged-in users
 * only; the backend enforces the same on the /chatbot routes.
 */
export function ProfessorElm() {
  const { user, error, isLoading } = useUser();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const authed = Boolean(user) && !error;

  // Pull server-stored history once, when the user first opens the window.
  useEffect(() => {
    if (!open || !authed || loaded) return;
    let cancelled = false;
    setLoaded(true);
    fetchChatHistory().then((history) => {
      if (cancelled || history.length === 0) return;
      // Only apply the fetched history if the user has not started chatting
      // while the request was in flight — otherwise it would wipe their
      // messages out.
      setTurns((prev) => (prev.length > 0 ? prev : history));
      setMessages((prev) =>
        prev.length > 0
          ? prev
          : history.flatMap((t) => [
              { role: 'user' as const, text: t.user },
              { role: 'assistant' as const, text: t.assistant },
            ])
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open, authed, loaded]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = useCallback(
    async (raw: string) => {
      const prompt = raw.trim();
      if (!prompt || sending || prompt.length > 2000) return;
      setSending(true);
      setDraft('');
      setMessages((prev) => [...prev, { role: 'user', text: prompt }]);
      try {
        const result = await sendChatTurn(prompt, turns);
        setTurns((prev) => [...prev, { user: prompt, assistant: result.message }]);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: result.message, imgURL: result.imgURL },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: chatErrorMessage(err).message, isError: true },
        ]);
      } finally {
        setSending(false);
      }
    },
    [sending, turns]
  );

  // Re-run the turn that failed. The failed turn (the user's question and the
  // error bubble) is dropped first, so the retry replaces it instead of
  // stacking a second copy underneath.
  const retry = useCallback(
    async (userText: string, userIndex: number, errorIndex: number) => {
      setMessages((prev) => prev.filter((_, i) => i !== userIndex && i !== errorIndex));
      await send(userText);
    },
    [send]
  );

  const handleNewConversation = async () => {
    setMessages([]);
    setTurns([]);
    setDraft('');
    try {
      await clearChatHistory();
    } catch {
      // Local reset already happened; the server forgetting is best-effort.
    }
  };

  if (isLoading || !authed) return null;

  return (
    <>
      <button
        type="button"
        className="beta-elm-fab"
        aria-label={open ? 'Close Professor Elm' : 'Chat with Professor Elm'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <IconX size={22} /> : <IconRobot size={22} />}
      </button>

      {open && (
        <div className="beta-elm-scrim" aria-hidden onClick={() => setOpen(false)} />
      )}

      {open && (
        <div className="beta-elm-panel" role="dialog" aria-label="Professor Elm">
          <div className="beta-elm-head">
            <Group gap={8}>
              <Avatar />
              <div>
                <Text size="sm" fw={600} c="var(--beta-text)">
                  Professor Elm
                </Text>
                <Text size="xs" c="var(--beta-text-3)">
                  {sending ? 'thinking…' : 'Knows your collection'}
                </Text>
              </div>
            </Group>
            <Group gap={4}>
              <Button
                size="xs"
                variant="subtle"
                aria-label="New conversation"
                leftSection={<IconEraser size={13} />}
                onClick={() => void handleNewConversation()}
                disabled={sending}
              >
                New
              </Button>
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <IconX size={15} />
              </ActionIcon>
            </Group>
          </div>

          <div className="beta-elm-msgs" ref={scrollRef}>
            {messages.length === 0 && !sending ? (
              <div style={{ padding: '32px 8px', textAlign: 'center' }}>
                <Text size="sm" c="var(--beta-text-3)" mb={10}>
                  Ask me about your collection. Try one of these:
                </Text>
                <Group gap={6} wrap="wrap" justify="center">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="beta-elm-suggestion"
                      onClick={() => void send(s)}
                    >
                      {s}
                    </button>
                  ))}
                </Group>
              </div>
            ) : (
              <>
                {messages.map((m, i) => {
                  const userIndex = m.isError ? nearestUserIndex(messages, i) : -1;
                  const retryText = userIndex >= 0 ? messages[userIndex].text : '';
                  return (
                    <Group
                      key={i}
                      justify={m.role === 'user' ? 'flex-end' : 'flex-start'}
                      wrap="nowrap"
                      styles={{ root: { width: '100%' } }}
                    >
                      {m.role === 'assistant' && <Avatar />}
                      <div
                        className={`beta-elm-bubble${m.role === 'user' ? ' user' : ''}${
                          m.isError ? ' error' : ''
                        }`}
                      >
                        {m.imgURL ? (
                          <img
                            src={m.imgURL}
                            alt=""
                            style={{
                              width: 52,
                              height: 68,
                              objectFit: 'cover',
                              borderRadius: 5,
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : null}
                        {m.isError && retryText ? (
                          <div>
                            <span>{m.text}</span>
                            <div style={{ marginTop: 6 }}>
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                onClick={() => void retry(retryText, userIndex, i)}
                                disabled={sending}
                              >
                                Try again
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <span>{m.text}</span>
                        )}
                      </div>
                    </Group>
                  );
                })}
                {sending && (
                  <Group justify="flex-start" styles={{ root: { width: '100%' } }}>
                    <Avatar />
                    <div className="beta-elm-bubble">
                      <TypingDots />
                    </div>
                  </Group>
                )}
              </>
            )}
          </div>

          <div className="beta-elm-input">
            <Textarea
              ref={inputRef}
              aria-label="Message for Professor Elm"
              placeholder="Ask about your collection…"
              value={draft}
              maxLength={2000}
              onChange={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                // Enter sends (including Cmd/Ctrl+Enter); Shift+Enter is a newline.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
              autosize
              minRows={1}
              maxRows={4}
              style={{ flex: 1 }}
              disabled={sending}
            />
            <Button
              leftSection={<IconSend size={13} />}
              onClick={() => void send(draft)}
              loading={sending}
              disabled={draft.trim().length === 0}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
